"use client";

import { c as _c } from "react/compiler-runtime";
import { useQuery } from "@tanstack/react-query";
import React from "react";
import { FiX } from "react-icons/fi";
import { RiCheckLine } from "react-icons/ri";
import { readSupplyOptions } from "@/components/question-editor/supply-options";
import Badge from "@/components/ui/badge";
import Button from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import QuantityStepper from "@/components/ui/quantity-stepper";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { SearchSelect, SearchSelectContent, SearchSelectItem, SearchSelectTrigger, SearchSelectValue, useSearchSelect } from "@/components/ui/select/search-select";
import { Skeleton } from "@/components/ui/skeleton";
import { MAX_SUPPLY_QUANTITY, type SupplyRowStatus, lotLabel, pendingSummary, readSupplySelections, selectionsFrom, supplyRowStatus } from "@/lib/supplies-used";
import { cx } from "@/lib/tailwind-utils";
import type { SuppliesUsed, SupplyLotOption, SupplyOption, SupplySelection } from "@/types/forms/question";
import type { NoteAnswerDetail } from "@/types/notes";
import { getNoteSupplies } from "@/api/provider/scribe/notes";
interface SuppliesUsedAnswerEditorProps {
  answer: NoteAnswerDetail;
  onChange: (answerId: string, data: Record<string, unknown>) => void;
  disabled?: boolean;
  title?: string;
  questionData?: SuppliesUsed;
  /** Absent in template previews, where there is no stock. */
  noteId?: string;
}
export const SuppliesUsedAnswerEditor: React.FC<SuppliesUsedAnswerEditorProps> = t0 => {
  const $ = _c(27);
  const {
    answer,
    onChange,
    disabled: t1,
    title,
    questionData,
    noteId
  } = t0;
  const disabled = t1 === undefined ? false : t1;
  let t2;
  let t3;
  if ($[0] !== answer.questionId || $[1] !== noteId) {
    t2 = ["notes", noteId, "supplies", answer.questionId];
    t3 = () => getNoteSupplies(noteId, answer.questionId);
    $[0] = answer.questionId;
    $[1] = noteId;
    $[2] = t2;
    $[3] = t3;
  } else {
    t2 = $[2];
    t3 = $[3];
  }
  const t4 = !!noteId;
  let t5;
  if ($[4] !== t2 || $[5] !== t3 || $[6] !== t4) {
    t5 = {
      queryKey: t2,
      queryFn: t3,
      enabled: t4
    };
    $[4] = t2;
    $[5] = t3;
    $[6] = t4;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  const query = useQuery(t5);
  const state = query.data ?? undefined;
  let t6;
  if ($[8] !== answer.data || $[9] !== answer.id || $[10] !== disabled || $[11] !== noteId || $[12] !== onChange || $[13] !== query || $[14] !== questionData || $[15] !== state || $[16] !== title) {
    const taken = state?.supplies ?? [];
    let t7;
    if ($[18] !== state?.lotsByStockId) {
      t7 = stockId => state?.lotsByStockId[stockId] ?? [];
      $[18] = state?.lotsByStockId;
      $[19] = t7;
    } else {
      t7 = $[19];
    }
    const lotsFor = t7;
    const takenFor = stockId_0 => taken.find(t => t.stockId === stockId_0);
    let t8;
    if ($[20] !== lotsFor || $[21] !== takenFor) {
      t8 = row => ({
        ...row,
        shipmentId: row.shipmentId ?? takenFor(row.stockId)?.shipmentId ?? lotsFor(row.stockId)[0]?.shipmentId ?? null
      });
      $[20] = lotsFor;
      $[21] = takenFor;
      $[22] = t8;
    } else {
      t8 = $[22];
    }
    const draft = (readSupplySelections(answer.data) ?? selectionsFrom(taken)).map(t8);
    const options = readSupplyOptions(questionData);
    const canSync = stockId_1 => options.some(o => o.stockId === stockId_1) || !!takenFor(stockId_1);
    const isLatest = !!state && state.latestNoteId === noteId;
    const pending = isLatest ? pendingSummary(draft.filter(row_0 => canSync(row_0.stockId)), taken) : null;
    let t9;
    if ($[23] !== answer.data || $[24] !== answer.id || $[25] !== onChange) {
      t9 = next => onChange(answer.id, {
        ...answer.data,
        supplies: next
      });
      $[23] = answer.data;
      $[24] = answer.id;
      $[25] = onChange;
      $[26] = t9;
    } else {
      t9 = $[26];
    }
    const setDraft = t9;
    const readOnly = disabled || !noteId || !!state && !isLatest;
    const handleAdd = stockId_2 => {
      const option = options.find(o_0 => o_0.stockId === stockId_2);
      if (!option || draft.some(row_1 => row_1.stockId === stockId_2)) {
        return;
      }
      const record = takenFor(stockId_2);
      const row_2 = record ? selectionsFrom([record])[0] : {
        stockId: stockId_2,
        label: option.label,
        quantity: 1,
        shipmentId: lotsFor(stockId_2)[0]?.shipmentId ?? null,
        lotNumber: lotsFor(stockId_2)[0]?.lotNumber ?? null
      };
      setDraft([...draft, row_2]);
    };
    const changeRow = (stockId_3, changes) => setDraft(draft.map(row_3 => row_3.stockId === stockId_3 ? {
      ...row_3,
      ...changes
    } : row_3));
    t6 = <div className="space-y-2">{(title || readOnly && pending) && <div className="flex items-center gap-2">{title && <Label className="text-body-strong">{title}</Label>}{readOnly && pending && <Badge variant="warning" title={pending}>Not updated in inventory</Badge>}</div>}{!readOnly && <SearchSelect value="" onValueChange={handleAdd}><SearchSelectTrigger className="sm:w-96" disabled={query.isPending}><SearchSelectValue placeholder="Add a supply" /></SearchSelectTrigger><SearchSelectContent searchPlaceholder="Search supplies..." className="w-96 min-w-0 max-w-[calc(100vw-2rem)]"><SupplyOptionList options={options} addedIds={draft.map(_temp)} canTake={stockId_4 => lotsFor(stockId_4).length > 0 || !!takenFor(stockId_4)} /></SearchSelectContent></SearchSelect>}{noteId && query.isPending ? <Skeleton className="h-14 w-full" /> : draft.length === 0 ? readOnly ? <p className="text-body text-foreground-muted">—</p> : <p className="text-caption text-foreground-muted">{options.length === 0 ? "No supplies are set up on this template yet." : "Add what you used. Stock is taken when you save the note. Supplies are not billed."}</p> : <ul className="divide-y divide-border">{draft.map(row_5 => <SupplyRow key={row_5.stockId} row={row_5} lots={lotsFor(row_5.stockId)} status={!isLatest ? undefined : canSync(row_5.stockId) ? supplyRowStatus(row_5, takenFor(row_5.stockId)) : {
          tone: "warning",
          text: "No longer on this template"
        }} readOnly={readOnly} onChange={changes_0 => changeRow(row_5.stockId, changes_0)} onRemove={() => setDraft(draft.filter(r => r.stockId !== row_5.stockId))} />)}</ul>}{!readOnly && (query.isError || pending || taken.length > 0) && <div className="flex min-h-9 items-center gap-3 border-t border-border pt-3"><div className="min-w-0 flex-1 text-caption">{query.isError ? <span className="flex items-center gap-2 text-status-error-text"><span>Couldn't check inventory.</span><button type="button" onClick={() => query.refetch()} className="text-link underline hover:text-link-hover">Try again</button></span> : pending ? <span className="text-foreground-secondary">{`When you save: ${pending.charAt(0).toLowerCase()}${pending.slice(1)}`}</span> : <span className="flex items-center gap-1 text-status-success-text"><RiCheckLine className="size-4 shrink-0" /><span>Inventory up to date</span></span>}</div></div>}</div>;
    $[8] = answer.data;
    $[9] = answer.id;
    $[10] = disabled;
    $[11] = noteId;
    $[12] = onChange;
    $[13] = query;
    $[14] = questionData;
    $[15] = state;
    $[16] = title;
    $[17] = t6;
  } else {
    t6 = $[17];
  }
  return t6;
};
function SupplyOptionList(t0) {
  const $ = _c(14);
  const {
    options,
    addedIds,
    canTake
  } = t0;
  const {
    searchTerm
  } = useSearchSelect();
  let t1;
  let t2;
  if ($[0] !== addedIds || $[1] !== canTake || $[2] !== options || $[3] !== searchTerm) {
    t2 = Symbol.for("react.early_return_sentinel");
    bb0: {
      let t3;
      if ($[6] !== searchTerm) {
        t3 = option => option.label.toLowerCase().includes(searchTerm.toLowerCase());
        $[6] = searchTerm;
        $[7] = t3;
      } else {
        t3 = $[7];
      }
      const matches = options.filter(t3);
      if (matches.length === 0) {
        let t4;
        if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
          t4 = <div className="px-3 py-2 text-body text-muted-foreground">No supplies found.</div>;
          $[8] = t4;
        } else {
          t4 = $[8];
        }
        t2 = t4;
        break bb0;
      }
      let t4;
      if ($[9] !== addedIds || $[10] !== canTake) {
        t4 = option_0 => {
          const isAdded = addedIds.includes(option_0.stockId);
          const canAdd = !isAdded && canTake(option_0.stockId);
          return <SearchSelectItem key={option_0.stockId} value={option_0.stockId} showCheckmark={false} className={cx(!canAdd && "pointer-events-none")}><span className="flex w-full items-center justify-between gap-3"><span className={cx("min-w-0 truncate", canAdd ? "font-medium text-foreground" : "text-foreground-muted")}>{option_0.label}</span>{isAdded ? <RiCheckLine className="size-5 shrink-0 text-foreground" /> : canAdd ? <span className="size-5 shrink-0" /> : <span className="shrink-0 text-caption text-foreground-muted">None in stock</span>}</span></SearchSelectItem>;
        };
        $[9] = addedIds;
        $[10] = canTake;
        $[11] = t4;
      } else {
        t4 = $[11];
      }
      t1 = matches.map(t4);
    }
    $[0] = addedIds;
    $[1] = canTake;
    $[2] = options;
    $[3] = searchTerm;
    $[4] = t1;
    $[5] = t2;
  } else {
    t1 = $[4];
    t2 = $[5];
  }
  if (t2 !== Symbol.for("react.early_return_sentinel")) {
    return t2;
  }
  let t3;
  if ($[12] !== t1) {
    t3 = <>{t1}</>;
    $[12] = t1;
    $[13] = t3;
  } else {
    t3 = $[13];
  }
  return t3;
}
const STATUS_TONE: Record<SupplyRowStatus["tone"], string> = {
  success: "text-status-success-text",
  muted: "text-foreground-muted",
  warning: "text-status-warning-text"
};
const SupplyRow: React.FC<{
  row: SupplySelection;
  lots: SupplyLotOption[];
  /** Undefined until the note's stock loads. */
  status: SupplyRowStatus | undefined;
  readOnly: boolean;
  onChange: (changes: Partial<SupplySelection>) => void;
  onRemove: () => void;
}> = t0 => {
  const $ = _c(63);
  const {
    row,
    lots,
    status,
    readOnly,
    onChange,
    onRemove
  } = t0;
  if (readOnly) {
    let t1;
    if ($[0] !== row.label) {
      t1 = <span>{row.label}</span>;
      $[0] = row.label;
      $[1] = t1;
    } else {
      t1 = $[1];
    }
    let t2;
    if ($[2] !== row.quantity) {
      t2 = row.quantity > 1 && <span className="ml-1 text-caption text-foreground-muted">{`· ${row.quantity} units`}</span>;
      $[2] = row.quantity;
      $[3] = t2;
    } else {
      t2 = $[3];
    }
    let t3;
    if ($[4] !== t1 || $[5] !== t2) {
      t3 = <p className="truncate text-body-strong text-foreground">{t1}{t2}</p>;
      $[4] = t1;
      $[5] = t2;
      $[6] = t3;
    } else {
      t3 = $[6];
    }
    let t4;
    if ($[7] !== row.lotNumber) {
      t4 = row.lotNumber && <p className="truncate text-caption text-foreground-muted">{`Lot ${row.lotNumber}`}</p>;
      $[7] = row.lotNumber;
      $[8] = t4;
    } else {
      t4 = $[8];
    }
    let t5;
    if ($[9] !== t3 || $[10] !== t4) {
      t5 = <li className="py-2.5">{t3}{t4}</li>;
      $[9] = t3;
      $[10] = t4;
      $[11] = t5;
    } else {
      t5 = $[11];
    }
    return t5;
  }
  let t1;
  if ($[12] !== row.label) {
    t1 = <p className="truncate text-body-strong text-foreground">{row.label}</p>;
    $[12] = row.label;
    $[13] = t1;
  } else {
    t1 = $[13];
  }
  const t2 = row.shipmentId ?? undefined;
  let t3;
  if ($[14] !== lots || $[15] !== onChange) {
    t3 = shipmentId => onChange({
      shipmentId,
      lotNumber: lots.find(lot => lot.shipmentId === shipmentId)?.lotNumber ?? null
    });
    $[14] = lots;
    $[15] = onChange;
    $[16] = t3;
  } else {
    t3 = $[16];
  }
  const t4 = `Lot for ${row.label}`;
  let t5;
  if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <SelectValue placeholder="No lot in stock" />;
    $[17] = t5;
  } else {
    t5 = $[17];
  }
  let t6;
  if ($[18] !== t4) {
    t6 = <SelectTrigger className="h-8 w-auto max-w-full text-caption" aria-label={t4}>{t5}</SelectTrigger>;
    $[18] = t4;
    $[19] = t6;
  } else {
    t6 = $[19];
  }
  let t7;
  if ($[20] !== lots || $[21] !== row.lotNumber || $[22] !== row.shipmentId) {
    t7 = row.shipmentId && !lots.some(lot_0 => lot_0.shipmentId === row.shipmentId) && <SelectItem value={row.shipmentId}>{row.lotNumber ? `Lot ${row.lotNumber}` : "Lot in use"}</SelectItem>;
    $[20] = lots;
    $[21] = row.lotNumber;
    $[22] = row.shipmentId;
    $[23] = t7;
  } else {
    t7 = $[23];
  }
  let t8;
  if ($[24] !== lots) {
    t8 = lots.map(_temp2);
    $[24] = lots;
    $[25] = t8;
  } else {
    t8 = $[25];
  }
  let t9;
  if ($[26] !== t7 || $[27] !== t8) {
    t9 = <SelectContent>{t7}{t8}</SelectContent>;
    $[26] = t7;
    $[27] = t8;
    $[28] = t9;
  } else {
    t9 = $[28];
  }
  let t10;
  if ($[29] !== t2 || $[30] !== t3 || $[31] !== t6 || $[32] !== t9) {
    t10 = <Select value={t2} onValueChange={t3}>{t6}{t9}</Select>;
    $[29] = t2;
    $[30] = t3;
    $[31] = t6;
    $[32] = t9;
    $[33] = t10;
  } else {
    t10 = $[33];
  }
  const t11 = status && STATUS_TONE[status.tone];
  let t12;
  if ($[34] !== t11) {
    t12 = cx("flex h-4 min-w-0 items-center gap-1 text-caption", t11);
    $[34] = t11;
    $[35] = t12;
  } else {
    t12 = $[35];
  }
  let t13;
  if ($[36] !== status?.tone) {
    t13 = status?.tone === "success" && <RiCheckLine className="size-4 shrink-0" />;
    $[36] = status?.tone;
    $[37] = t13;
  } else {
    t13 = $[37];
  }
  const t14 = status?.text;
  let t15;
  if ($[38] !== t14) {
    t15 = <span className="truncate">{t14}</span>;
    $[38] = t14;
    $[39] = t15;
  } else {
    t15 = $[39];
  }
  let t16;
  if ($[40] !== t12 || $[41] !== t13 || $[42] !== t15) {
    t16 = <p className={t12}>{t13}{t15}</p>;
    $[40] = t12;
    $[41] = t13;
    $[42] = t15;
    $[43] = t16;
  } else {
    t16 = $[43];
  }
  let t17;
  if ($[44] !== t1 || $[45] !== t10 || $[46] !== t16) {
    t17 = <div className="min-w-0 flex-1 space-y-1">{t1}{t10}{t16}</div>;
    $[44] = t1;
    $[45] = t10;
    $[46] = t16;
    $[47] = t17;
  } else {
    t17 = $[47];
  }
  let t18;
  if ($[48] !== onChange) {
    t18 = quantity => onChange({
      quantity
    });
    $[48] = onChange;
    $[49] = t18;
  } else {
    t18 = $[49];
  }
  const t19 = `Quantity for ${row.label}`;
  let t20;
  if ($[50] !== row.quantity || $[51] !== t18 || $[52] !== t19) {
    t20 = <QuantityStepper max={MAX_SUPPLY_QUANTITY} value={row.quantity} onChange={t18} ariaLabel={t19} />;
    $[50] = row.quantity;
    $[51] = t18;
    $[52] = t19;
    $[53] = t20;
  } else {
    t20 = $[53];
  }
  const t21 = `Remove ${row.label}`;
  let t22;
  if ($[54] !== onRemove || $[55] !== t21) {
    t22 = <Button variant="ghost" size="sm" icon={FiX} onClick={onRemove} aria-label={t21} />;
    $[54] = onRemove;
    $[55] = t21;
    $[56] = t22;
  } else {
    t22 = $[56];
  }
  let t23;
  if ($[57] !== t20 || $[58] !== t22) {
    t23 = <div className="flex shrink-0 items-center gap-3">{t20}{t22}</div>;
    $[57] = t20;
    $[58] = t22;
    $[59] = t23;
  } else {
    t23 = $[59];
  }
  let t24;
  if ($[60] !== t17 || $[61] !== t23) {
    t24 = <li className="flex items-center gap-3 py-2.5">{t17}{t23}</li>;
    $[60] = t17;
    $[61] = t23;
    $[62] = t24;
  } else {
    t24 = $[62];
  }
  return t24;
};
function _temp(row_4) {
  return row_4.stockId;
}
function _temp2(lot_1) {
  return <SelectItem key={lot_1.shipmentId} value={lot_1.shipmentId}>{lotLabel(lot_1)}</SelectItem>;
}